import { AnyAction, Reducer } from 'redux';
import {
  deleteVersicherungsnehmerExceptisFinanzdienstleister,
  GlobalAction,
  GlobalActions,
  isAzam,
  ReplaceState,
  SavedState,
  SavedStateWrapper,
  WrappedSavedStates,
  WrappedSavedStatesLoaded
} from '@ps20-frontend/shop-integration-redux';
import {
  App,
  areAppTypesEquivalent,
  FeatureToggles,
  getInactiveProductApps,
  isBeibehalten,
  isEntryProtectedOrActive,
  ZuersZoneEnum,
} from '@ps20-frontend/frontend-models';
import { Zahlungsart } from '@ps20/bff-models';
import { isExtremwetterUWProcessNecessary, ExtremwetterUWProcessQuery } from '@ps20-frontend/selector';
import { migrateAngebotsdatumArray } from '@ps20-frontend/migrations';
import { loadAndMigrateCheckoutState } from '../migrations';
import { IAppState } from '../localStore';
import { Produkt, VwgCheckoutProdukt } from '../model';


function updatePremiumAndExtremwetterDisabled(
  produkte: VwgCheckoutProdukt[],
  vwgSavedStates: SavedStateWrapper[],
  featureToggles: FeatureToggles,
): Produkt[] {
  produkte
    ?.filter(
      produkt => areAppTypesEquivalent(produkt.appId, App.VWG) && produkt.premiumAndExtremwetterDisabled === undefined,
    )
    ?.forEach(produkt => {
      const loadedVwgAppState = vwgSavedStates?.find(
        savedVwgItem => savedVwgItem.angebotsId === produkt.angebotsId,
      );
      if (loadedVwgAppState) {
        const premiumAndExtremwetterDisabledSelectorTo =
          mapLoadedStateTopremiumAndExtremwetterDisabledSelectorTo(
            loadedVwgAppState.state,
            featureToggles,
          );
        produkt.premiumAndExtremwetterDisabled = isExtremwetterUWProcessNecessary(
          premiumAndExtremwetterDisabledSelectorTo,
        );
      }
    });
  return produkte;
}

function removeAzamRestrictedData(state: IAppState, loadedAppState: SavedStateWrapper): void {
  if (isAzam(state.transientState.role)) {
    deleteVersicherungsnehmerExceptisFinanzdienstleister(
      loadedAppState.state.versicherungsnehmer,
    );
    if (state.zahlung?.zahlungsart === Zahlungsart.MAKLER_INKASSO) {
      delete loadedAppState.state.zahlung.zahlungsart;
    }
  }
}

function filterLoadedEntriesByActiveApps(
  loadedAppState: SavedStateWrapper,
  featureToggles: FeatureToggles,
): Array<Produkt> {
  const inactiveApps = getInactiveProductApps(featureToggles);
  const rawProdukte = (loadedAppState.state.produkte as Array<Produkt>) ?? [];
  const protectedAngebotsIds = new Set(
    rawProdukte
      .filter(p => isBeibehalten(p) && !!p.angebotsId)
      .map(p => p.angebotsId),
  );
  const produkte = rawProdukte.filter(p =>
    isEntryProtectedOrActive(p, inactiveApps, protectedAngebotsIds),
  );
  loadedAppState.state.produkte = produkte;

  const vorversicherungen = (loadedAppState.state.vorversicherungen ?? []).filter(v =>
    isEntryProtectedOrActive(v, inactiveApps, protectedAngebotsIds),
  );
  loadedAppState.state.vorversicherungen = vorversicherungen;
  return produkte;
}

function filterLoadedAngabenByProdukte(
  loadedAppState: SavedStateWrapper,
  produkte: Array<Produkt>,
): void {
  const produkteAngebotsIds = new Set(produkte.map(p => p.angebotsId));

  const tkvAngabenV2 = loadedAppState.state.tkvAngabenV2 as { angabenItems?: Array<{ angebotsId: string }> };
  if (tkvAngabenV2?.angabenItems) {
    tkvAngabenV2.angabenItems = tkvAngabenV2.angabenItems.filter(
      item => produkteAngebotsIds.has(item.angebotsId),
    );
  }

  const tkvAngaben = loadedAppState.state.tkvAngaben as { angabenListe?: Array<{ angebotsId: string }> };
  if (tkvAngaben?.angabenListe) {
    tkvAngaben.angabenListe = tkvAngaben.angabenListe.filter(
      item => produkteAngebotsIds.has(item.angebotsId),
    );
  }
}

function handleSavedStatesLoaded(state: IAppState, action: AnyAction): IAppState {
  const actionPayload: WrappedSavedStates = (action as GlobalAction<WrappedSavedStatesLoaded>)
    .payload.savedStatesLoaded;
  const loadedAppState = actionPayload.find(
    loadedState => loadedState.appId === App.SHOP_CHECKOUT,
  );
  const featureToggles = state.transientState.featureToggles;
  if (loadedAppState != null) {
    removeAzamRestrictedData(state, loadedAppState);
    const produkte = filterLoadedEntriesByActiveApps(loadedAppState, featureToggles);
    filterLoadedAngabenByProdukte(loadedAppState, produkte);

    migrateAngebotsdatumArray(produkte);

    const newState = loadAndMigrateCheckoutState(state, loadedAppState.state);
    newState.versicherungsnehmer.vnLoadedFromSavedAntrag = true;
    newState.versicherungData = state.versicherungData;
    if (newState.tkvAngabenV2 && !newState.tkvAngabenV2.dogBreeds?.length) {
      newState.tkvAngabenV2.dogBreeds = state.tkvAngabenV2?.dogBreeds ?? [];
      newState.tkvAngabenV2.horseBreeds = state.tkvAngabenV2?.horseBreeds ?? [];
      newState.tkvAngabenV2.catBreeds = state.tkvAngabenV2?.catBreeds ?? [];
    }

    const savedVwgStates = actionPayload.filter(
      loadedAppState => areAppTypesEquivalent(loadedAppState.appId, App.VWG),
    );
    if (savedVwgStates) {
      updatePremiumAndExtremwetterDisabled(newState.produkte, savedVwgStates, featureToggles);
    }
    return newState;
  } else {
    return state;
  }
}

export const rootReducer =
  (reducer: Reducer) =>
  (state: IAppState, action: AnyAction): IAppState => {
    switch (action.type) {
      case GlobalActions.SAVED_STATES_LOADED:
        return handleSavedStatesLoaded(state, action);
      case GlobalActions.REPLACE_STATE: {
        const actionPayload: ReplaceState<IAppState> = action.payload;
        if (actionPayload.appId === App.SHOP_CHECKOUT) {
          const replacedState = actionPayload.appState;
          if (replacedState.tkvAngabenV2 && !replacedState.tkvAngabenV2.dogBreeds?.length) {
            replacedState.tkvAngabenV2.dogBreeds = state.tkvAngabenV2?.dogBreeds ?? [];
            replacedState.tkvAngabenV2.horseBreeds = state.tkvAngabenV2?.horseBreeds ?? [];
            replacedState.tkvAngabenV2.catBreeds = state.tkvAngabenV2?.catBreeds ?? [];
          }
          return replacedState;
        }
        return state;
      }
      case GlobalActions.SAVE_STATUS_RECEIVED: {
        return {
          ...state,
          dokumentId: action.payload.dokumentId,
        };
      }
      case GlobalActions.SAVED_DOKUMENTID_RECEIVED: {
        if (!action.payload) {
          return state;
        }
        return {
          ...state,
          dokumentId: action.payload,
        };
      }
      default:
        return reducer(state, action);
    }
  };

function mapLoadedStateTopremiumAndExtremwetterDisabledSelectorTo(
  loadedVwgAppState: SavedState,
  featureToggles: FeatureToggles,
): ExtremwetterUWProcessQuery {
  const elementarSelbstbehalt = loadedVwgAppState.elementarSelbstbehalt as unknown as {
    id: ZuersZoneEnum;
  };
  const vorbelegungen = loadedVwgAppState.vorbelegungen as unknown as {
    activateElementar: boolean;
  };
  const gisDaten = loadedVwgAppState.gisDaten as unknown as { flutzone: number };
  const vorschaeden = loadedVwgAppState.vorschaeden as unknown as {
    elementarVorschaden: { countFromRisikoortService: number; count: number };
    vorschaedenVorhanden: boolean;
  };
  const risikoort = loadedVwgAppState.risikoort as unknown as { zuerszone: number };

  return {
    elementarSelbstbehaltId: elementarSelbstbehalt.id,
    activateElementar: vorbelegungen?.activateElementar,
    flutzone: gisDaten?.flutzone,
    featureToggles: featureToggles,
    vorschaedenVorhanden: vorschaeden?.vorschaedenVorhanden,
    vertragsoptionen: loadedVwgAppState.vertragsoptionen,
    elementarVorschadenCountFromRisikoortService:
      vorschaeden?.elementarVorschaden.countFromRisikoortService,
    risikoortZuerszone: risikoort.zuerszone,
    elementarVorschaedenCount: vorschaeden?.elementarVorschaden?.count,
  };
}

